import React, { useState } from 'react'
import clsx from 'clsx'
import { approve, deny } from './api'
import DomePin from '/app/assets/images/domecile_pin.png'

function formatDate(isoDate) {
  if (!isoDate) return '—'
  const dateOnly = isoDate.split('T')[0]
  return new Date(dateOnly + 'T12:00:00').toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  })
}

function RequestInfo({ requesterName, currentDeadline, desiredDeadline, reason, address, unit, cityStateZip, isUpdatedRequest = false }) {
  return (
    <div className="extension-approval-modal__info dome-d-flex dome-flex-column dome-gap24 dome-color-dark-grey">
      <div className="extension-approval-modal__info-row dome-p1">
        {requesterName} is requesting an extension to the board's decision deadline.
      </div>
      <div className="extension-approval-modal__info-row dome-line-height20 dome-p1 dome-color-dark-grey dome-d-flex dome-flex-column dome-gap6">
        <div>Current deadline: {currentDeadline}</div>
        <div className='dome-d-flex dome-align-center dome-gap18'>
          <div className='dome-color-darkest-grey dome-text-w500'>Desired deadline: {desiredDeadline}</div>
          {isUpdatedRequest && (
            <div className="extension-approval-modal__updated-badge dome-d-flex dome-align-center dome-gap6 dome-p3 dome-color-aqua dome-padding3-6 dome-bg-accent dome-rounded-border">
              <i className='fal fa-alarm-exclamation' />
              <span>This is an updated request.</span>
            </div>
          )}
        </div>
        {reason && <div className="extension-approval-modal__reason">Reason: {reason}</div>}
      </div>
      <div className="extension-approval-modal__info-row dome-d-flex dome-align-center dome-gap24 dome-bg-fill dome-rounded-border">
        <div className='map-pin__bg dome-relative dome-rounded-border' style={{width: 113, height: 95}}>
          <img className='map-pin__image dome-absolute' src={DomePin} />
        </div>
        <div className='dome-h4 dome-text-w400'>
          <div>{address}</div>
          <div className='dome-text-w500'>{unit}</div>
          <div>{cityStateZip}</div>
        </div>
      </div>
    </div>
  )
}

function DecisionForm({ requestId, packageHashedId, onSuccess }) {
  const [decision,   setDecision]   = useState(null)
  const [denyReason, setDenyReason] = useState('')
  const [loading,    setLoading]    = useState(false)
  const [error,      setError]      = useState(null)

  const handleSubmit = async () => {
    if (!decision || loading) return
    setLoading(true)
    setError(null)
    try {
      if (decision === 'approve') {
        await approve(packageHashedId, requestId)
      } else {
        await deny(packageHashedId, requestId, denyReason)
      }
      onSuccess(decision)
    } catch {
      setError('Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="extension-approval-modal__form">
      <div className='dome-d-flex dome-flex-column dome-gap6'>
        <div className="extension-approval-modal__radios dome-d-flex dome-align-center dome-gap12">
          <label className="extension-approval-modal__radios-container dome-rounded-border dome-d-flex dome-align-center dome-gap9 dome-cursor-pointer dome-flex-1">
            <input
              type="radio"
              name="extension-decision"
              className={decision === 'keep' ? 'checked-keep' : ''}
              value={'keep'}
              checked={decision === 'keep'}
              onChange={() => setDecision('keep')}
            />
            <div className="dome-p2 decision-text dome-color-dark-grey">keep current deadline</div>
          </label>
          <label className="extension-approval-modal__radios-container dome-rounded-border dome-d-flex dome-align-center dome-gap9 dome-cursor-pointer dome-flex-1">
            <input
              type="radio"
              name="extension-decision"
              className={decision === 'approve' ? 'checked-approve' : ''}
              value={'approve'}
              checked={decision === 'approve'}
              onChange={() => setDecision('approve')}
            />
            <div className="dome-p2 decision-text dome-color-dark-grey">approve new deadline</div>
          </label>
        </div>
        {decision === 'keep' && (
          <div className="extension-approval-modal__deny-reason">
            <label htmlFor="deny-reason" className="dome-p2 dome-color-dark-grey">
              <div>reason</div>
              <textarea
                id="deny-reason"
                className="dome-input"
                value={denyReason}
                placeholder='Enter a reason (optional)'
                onChange={(e) => setDenyReason(e.target.value)}
                rows={1}
                maxLength={250}
              />
            </label>
          </div>
        )}
      </div>

      <div className='dome-p3 dome-dark-grey'>An extended deadline does not imply consent or denial to this application.</div>

      {error && <p className="dome-p3 dome-color-red-alert">{error}</p>}

      <div className='dome-d-flex dome-align-center dome-justify-between'>
        <a href="/" className='dome-link dome-link-cancel dome-color-med-grey'> return to dashboard</a>
        <button
          className={clsx('dome-btn dome-btn-base', decision ? 'dome-btn-dark-green' : 'dome-btn-disabled')}
          disabled={!decision || loading}
          onClick={handleSubmit}
        >
          {loading ? 'sending…' : 'send reply'}
        </button>
      </div>
    </div>
  )
}

function SuccessState({ decision, packageHashedId, desiredDeadline, currentDeadline }) {
  const approved = decision === 'approve'

  return (
    <div className="extension-approval-modal__success dome-d-flex dome-flex-column dome-align-center dome-gap24">
      <div className='dome-d-flex dome-flex-column dome-gap6'>
        <div className="dome-h2 dome-color-dark-green">
          your response has been sent
        </div>
        <div className="dome-p1">
          {approved
            ? `The decision deadline is now ${desiredDeadline}.`
            : `The decision deadline remains ${currentDeadline}.`}
        </div>
      </div>
      <div className="dome-d-flex dome-gap12">
        <a href="/" className="dome-link dome-is-button dome-link-grey-stroke">
          back to dashboard
        </a>
        <a href={`/packages/${packageHashedId}`} className="dome-link dome-is-button dome-link-dark-green">
          go to application
        </a>
      </div>
    </div>
  )
}

function ExpiredState() {
  return (
    <div className="extension-approval-modal">
      <div className="dome-h2 dome-color-red-alert">request expired</div>
      <div className="extension-approval-modal__info dome-d-flex dome-flex-column dome-gap24 dome-color-dark-grey">
        <div className="dome-p1">Contact property management for more information.</div>
        <div className='dome-d-flex dome-align-center dome-justify-start'>
          <a href="/" className='dome-link dome-link-cancel dome-color-med-grey'> return to dashboard</a>
        </div>
      </div>
    </div>
  )
}

export default function ExtensionApprovalModal({
  requestId,
  packageHashedId,
  requesterName,
  currentDeadline,
  desiredDeadline,
  reason,
  address,
  unit,
  cityStateZip,
  isUpdatedRequest = false,
  state,
}) {
  const [successDecision, setSuccessDecision] = useState(null)

  if (state === 'expired') return <ExpiredState />

  if (successDecision) {
    return <SuccessState decision={successDecision} packageHashedId={packageHashedId} desiredDeadline={desiredDeadline} currentDeadline={currentDeadline} />
  }

  return (
    <div className="extension-approval-modal">
      <div className="dome-h2 dome-color-dark-green">deadline extension request</div>
      <RequestInfo
        requesterName={requesterName}
        currentDeadline={currentDeadline}
        desiredDeadline={desiredDeadline}
        reason={reason}
        address={address}
        unit={unit}
        cityStateZip={cityStateZip}
        isUpdatedRequest={isUpdatedRequest}
      />
      <DecisionForm
        requestId={requestId}
        packageHashedId={packageHashedId}
        onSuccess={setSuccessDecision}
      />
    </div>
  )
}
